import { LanguagesEnum } from '@/api/enums/languages.enum';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  CourseContentType,
  CourseFormState,
  Slide,
  SlideDeck,
  SlideTemplateType,
  SlideType as SlideTypeEnum,
} from '@/client/courses/types/courses.type';
import { useToast } from '@/hooks/useToast';
import {
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { v4 } from 'uuid';

type UseSlideDeckStateParams = {
  index: number;
  values: CourseFormState;
  visible: boolean;
  setFieldValue: (field: string, value: any) => void;
  onHide: () => void;
};

export const useSlideDeckState = ({
  index,
  values,
  visible,
  setFieldValue,
  onHide,
}: UseSlideDeckStateParams) => {
  const { id: courseId } = useParams();
  const { t } = useTranslation();
  const toast = useToast();

  const [selectedLanguage, setSelectedLanguage] = useState(
    values?.courseLanguages?.length === 1
      ? values?.courseLanguages?.[0]
      : LanguagesEnum.EN,
  );
  const [slidesByLanguage, setSlidesByLanguage] = useState<
    Record<string, Slide[]>
  >({});
  const [tempSlidesByLanguage, setTempSlidesByLanguage] = useState<
    Record<string, Slide[]>
  >({});

  const buildSlidesForLanguage = (currentLanguage: string) => {
    const presentation = values?.courseContents[index]?.presentation;
    const existingSlides = presentation?.translations?.find(
      ({ language }) => language === currentLanguage,
    )?.slides;

    return existingSlides?.length
      ? existingSlides.map((s) => ({
          ...s,
          id: s.id || `temp-${v4()}`,
          infobox: s.infobox || '',
          headline: s.headline || '',
          html: s.html || '',
          popupSlide: s.popupSlide || undefined,
          templateType: s.templateType || SlideTemplateType.TEXT_IMAGE,
          footnote: s.footnote || '',
          images: s.images || undefined,
          interactiveElement: s.interactiveElement || undefined,
          type: s.type || undefined,
        }))
      : [
          {
            id: `temp-${v4()}`,
            headline: t('content.slide', { number: 1 }),
            html: '',
            popupSlide: undefined,
            templateType: SlideTemplateType.TEXT_IMAGE,
            footnote: '',
            images: [
              {
                file: null,
                position: 0,
                withBorder: true,
                withZoom: false,
              },
            ],
            interactiveElement: undefined,
            type: SlideTypeEnum.NORMAL,
            position: 0,
            infobox: '',
          },
        ];
  };

  const initializeSlidesForLanguage = (
    currentLanguage: string,
    markAsSaved = false,
  ) => {
    if (!slidesByLanguage[currentLanguage]?.length) {
      const defaultSlides = buildSlidesForLanguage(currentLanguage);

      setSlidesByLanguage((prev) => ({
        ...prev,
        [currentLanguage]: defaultSlides,
      }));

      if (markAsSaved) {
        setTempSlidesByLanguage((prev) => ({
          ...prev,
          [currentLanguage]: defaultSlides,
        }));
      }
    }
  };

  const [loadingSlides, setLoadingSlides] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    if (!visible) return;

    const initialTitle =
      values?.courseContents[index]?.presentation?.title ||
      t('content.new.slidedeck');

    setPresentationTitle(initialTitle);
    setSavedTitle(initialTitle);
    setLoadFailed(false);
    resetSlideSelection();
  }, [visible, values?.id, index]);

  const retryLoad = () => {
    if (loadingSlides) return;

    setLoadFailed(false);
    setLoadingSlides(true);
    setLoadAttempt((attempt) => attempt + 1);
  };
  useEffect(() => {
    let cancelled = false;

    const fetchPresentation = async () => {
      setLoadingSlides(true);

      const presentation = values?.courseContents[index]?.presentation;
      const presentationId = presentation?.id;

      if (visible && presentationId && !presentationId.startsWith('temp')) {
        try {
          const fetched: SlideDeck = await client.courses.getSlideDeck(
            courseId || '',
            presentationId,
          );

          if (cancelled) return;

          // Populate slidesByLanguage from fetched presentation
          const newSlidesByLanguage: Record<string, Slide[]> = {};

          for (const translation of fetched?.translations ?? []) {
            newSlidesByLanguage[translation.language] = (
              translation.slides ?? []
            )
              .map((slide: Slide) => ({
                ...slide,
                id: slide.id || `temp-${v4()}`,
                infobox: slide.infobox || '',
                headline: slide.headline || '',
                html: slide.html || '',
                popupSlide: slide.popupSlide || undefined,
                templateType:
                  slide.templateType || SlideTemplateType.TEXT_IMAGE,
                footnote: slide.footnote || '',
                images: slide.images || undefined,
                type: slide.type || undefined,
                interactiveElement: slide.interactiveElement || undefined,
              }))
              .sort((a, b) => (a.position || 0) - (b.position || 0));
          }

          if (!newSlidesByLanguage[selectedLanguage]?.length) {
            const availableLanguages = Object.keys(newSlidesByLanguage);
            const fallbackLanguage =
              availableLanguages.find((language) =>
                values?.courseLanguages?.includes(language as LanguagesEnum),
              ) ?? availableLanguages[0];

            if (fallbackLanguage) {
              setSelectedLanguage(fallbackLanguage as LanguagesEnum);
            } else {
              newSlidesByLanguage[selectedLanguage] =
                buildSlidesForLanguage(selectedLanguage);
            }
          }

          setSlidesByLanguage(newSlidesByLanguage);
          setTempSlidesByLanguage(newSlidesByLanguage);
        } catch (error) {
          if (cancelled) return;

          setLoadFailed(true);
          handleAxiosError(error as AxiosError, toast);
        }
      } else {
        initializeSlidesForLanguage(selectedLanguage, true);
      }

      if (!cancelled) {
        setLoadingSlides(false);
      }
    };

    fetchPresentation();

    return () => {
      cancelled = true;
    };
  }, [visible, values?.id, index, loadAttempt]);

  const slides = slidesByLanguage[selectedLanguage] || [];
  const [currentSlideIdx, setCurrentSlideIdx] = useState(0);
  const [selectedSlideId, setSelectedSlideId] = useState<string | null>(null);
  const [isAnyDragging, setIsAnyDragging] = useState(false);
  const [isEditingPopUpSlide, setIsEditingPopUpSlide] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  const [presentationTitle, setPresentationTitle] = useState(
    values?.courseContents[index]?.presentation?.title ||
      t('content.new.slidedeck'),
  );
  const [savedTitle, setSavedTitle] = useState(presentationTitle);

  const resetSlideSelection = () => {
    setCurrentSlideIdx(0);
    setSelectedSlideId(null);
    setIsEditingPopUpSlide(false);
  };

  const handleLanguageChange = (language: LanguagesEnum) => {
    setSelectedLanguage(language);
    initializeSlidesForLanguage(language);
    resetSlideSelection();
  };

  const mainSlides = slides.filter(({ type }) => type === SlideTypeEnum.NORMAL);
  const popUpSlides = slides.filter(({ type }) => type === SlideTypeEnum.POPUP);

  const selectedSlide = selectedSlideId
    ? slides.find(({ id }) => id === selectedSlideId)
    : null;

  const currentSlideData = useMemo(() => {
    if (isEditingPopUpSlide && selectedSlide) {
      return selectedSlide;
    }
    return mainSlides[currentSlideIdx] || {};
  }, [
    isEditingPopUpSlide,
    selectedSlide,
    mainSlides,
    currentSlideIdx,
    selectedLanguage,
  ]);

  const isPurpleBackground =
    currentSlideData?.templateType === SlideTemplateType.SUMMARY;

  useEffect(() => {
    if (currentSlideIdx >= mainSlides.length && mainSlides.length > 0) {
      setCurrentSlideIdx(mainSlides.length - 1);
    }
  }, [mainSlides.length, currentSlideIdx]);

  const addSlide = (isPopUp = false) => {
    const newSlide = {
      id: `temp-${v4()}`,
      headline: isPopUp
        ? t('content.popup.slide', { number: popUpSlides.length + 1 })
        : t('content.slide', { number: mainSlides.length + 1 }),
      html: '',
      tempId: isPopUp ? v4() : undefined,
      popupSlide: undefined,
      templateType: SlideTemplateType.TEXT_IMAGE,
      footnote: '',
      images: [{ file: null, position: 0, withBorder: true, withZoom: false }],
      type: isPopUp ? SlideTypeEnum.POPUP : SlideTypeEnum.NORMAL,
      infobox: '',
    };

    setSlidesByLanguage((prev) => {
      const currentSlides = prev[selectedLanguage] ?? [];

      return {
        ...prev,
        [selectedLanguage]: [
          ...currentSlides,
          { ...newSlide, position: currentSlides.length },
        ],
      };
    });
  };

  const deleteSlide = (selectedSlideId?: string) => {
    const slideToDelete = slides.find(({ id }) => id === selectedSlideId);
    if (!slideToDelete) return;

    const isPopUp = slideToDelete.type === SlideTypeEnum.POPUP;
    let filteredSlides = slides.filter(({ id }) => id !== selectedSlideId);

    if (
      !isPopUp &&
      filteredSlides.filter((s) => s.type !== SlideTypeEnum.POPUP).length === 0
    ) {
      return;
    }

    if (isPopUp) {
      const deletedId = slideToDelete.id;
      const deletedTempId = slideToDelete.tempId;

      filteredSlides = filteredSlides.map((slide) => {
        const refsDeleted =
          (deletedId &&
            (typeof slide.popupSlide === 'string'
              ? slide.popupSlide === deletedId
              : slide.popupSlide?.id === deletedId)) ||
          (deletedTempId && slide.tempPopupSlideId === deletedTempId);

        return refsDeleted
          ? { ...slide, popupSlide: undefined, tempPopupSlideId: undefined }
          : slide;
      });
    }

    const renumberedSlides = filteredSlides.map((slide, position) => ({
      ...slide,
      position,
    }));

    setSlidesByLanguage((prev) => ({
      ...prev,
      [selectedLanguage]: renumberedSlides,
    }));

    const remainingMainSlides = renumberedSlides.filter(
      ({ type }) => type !== SlideTypeEnum.POPUP,
    );

    if (!isPopUp && currentSlideIdx >= remainingMainSlides.length) {
      setCurrentSlideIdx(remainingMainSlides.length - 1);
    }

    if (selectedSlideId === selectedSlide?.id) {
      setSelectedSlideId(null);
      setIsEditingPopUpSlide(false);
    }
  };

  const updateSlide = (selectedSlideId: string, updates: Partial<Slide>) => {
    const aiFields: (keyof Slide)[] = [
      'aiCustomizationInstructions',
      'isAiCustomizationEnabled',
    ];
    const hasAiFieldUpdates = aiFields.some((field) => field in updates);

    if (hasAiFieldUpdates) {
      const currentSlide = slides.find((s) => s.id === selectedSlideId);
      const slidePosition = currentSlide?.position;
      const aiUpdates = Object.fromEntries(
        Object.entries(updates).filter(([key]) =>
          aiFields.includes(key as keyof Slide),
        ),
      );

      setSlidesByLanguage((prev) => {
        const updated = { ...prev };
        for (const lang of Object.keys(updated)) {
          updated[lang] = updated[lang].map((slide) => {
            if (lang === selectedLanguage && slide.id === selectedSlideId) {
              return { ...slide, ...updates };
            }
            if (lang !== selectedLanguage && slide.position === slidePosition) {
              return { ...slide, ...aiUpdates };
            }
            return slide;
          });
        }
        return updated;
      });
    } else {
      setSlidesByLanguage((prev) => ({
        ...prev,
        [selectedLanguage]: (prev[selectedLanguage] ?? []).map((slide) =>
          slide.id === selectedSlideId ? { ...slide, ...updates } : slide,
        ),
      }));
    }
  };

  const handlePrevSlide = () => {
    if (currentSlideIdx > 0) setCurrentSlideIdx(currentSlideIdx - 1);
  };

  const handleNextSlide = () => {
    if (currentSlideIdx < mainSlides.length - 1)
      setCurrentSlideIdx(currentSlideIdx + 1);
  };

  const handleSlidesDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setIsAnyDragging(false);

    if (over && active.id !== over.id) {
      const from = slides.findIndex((s) => s.id === active.id);
      const to = slides.findIndex((s) => s.id === over.id);

      if (from !== -1 && to !== -1) {
        const currentSlideId = mainSlides[currentSlideIdx]?.id;

        const updatedSlides = arrayMove(slides, from, to).map(
          (slide, index) => ({
            ...slide,
            position: index,
          }),
        );

        setSlidesByLanguage((prev) => ({
          ...prev,
          [selectedLanguage]: updatedSlides,
        }));

        if (currentSlideId) {
          const newIndex = updatedSlides
            .filter(({ type }) => type === SlideTypeEnum.NORMAL)
            .findIndex((s) => s.id === currentSlideId);

          if (newIndex !== -1) {
            setCurrentSlideIdx(newIndex);
          }
        }
      }
    }
  };

  const handleSlidesDragCancel = () => {
    setIsAnyDragging(false);
  };

  const handleSlidesDragStart = () => {
    setIsAnyDragging(true);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const getCurrentEditingSlide = () => {
    if (isEditingPopUpSlide && selectedSlide) {
      return selectedSlide;
    }
    return currentSlideData;
  };

  const currentEditingSlide = getCurrentEditingSlide();

  const isSaveDisabled =
    loadFailed || loadingSlides || Object.keys(slidesByLanguage).length === 0;

  const handleSave = async () => {
    if (!courseId || isSaveDisabled) return;

    const presentationProps = {
      id: values?.courseContents[index]?.presentation?.id?.includes('temp-')
        ? undefined
        : values?.courseContents[index]?.presentation?.id,
      courseId,
      title: presentationTitle,
    };

    const allTranslations = Object.entries(slidesByLanguage).map(
      ([language, slides]) => ({
        language,
        slides: slides.map((slide, index) => ({
          ...slide,
          interactiveElement:
            slide.templateType === SlideTemplateType.GALLERY
              ? null
              : slide.interactiveElement,
          id: slide.id?.includes('temp-') ? undefined : slide.id,
          templateType: slide.templateType || SlideTemplateType.TEXT_IMAGE,
          images: slide.images
            ?.filter((image) => image.file !== null && image.file?.id !== null)
            ?.filter(
              (_, index) =>
                slide.templateType === SlideTemplateType.GALLERY || index === 0,
            )
            ?.map((image) => ({
              file: image.file?.id || null,
              position: image.position,
              title: image.title || '',
              description: image.description || '',
              withBorder: image.withBorder || false,
              withZoom: image.withZoom || false,
              withControls: image.withControls || false,
            })),
          html: slide.html || '',
          position: index,
          popupSlide:
            (typeof slide.popupSlide === 'string'
              ? slide.popupSlide
              : slide.popupSlide?.id) || undefined,
        })),
      }),
    );

    const payload = {
      ...presentationProps,
      translations: allTranslations,
    };

    try {
      const service = client.courses;
      let response;

      if (payload.id && !payload.id.includes('temp-')) {
        response = await service.updateSlideDeck(
          courseId,
          payload.id,
          payload as SlideDeck,
        );
      } else {
        response = await service.createSlideDeck(
          courseId,
          payload as SlideDeck,
        );
      }

      const updatedContents = [...values.courseContents];
      updatedContents[index] = {
        ...updatedContents[index],
        type: CourseContentType.PRESENTATION,
        presentation: response,
        position: index,
      };

      setFieldValue('courseContents', updatedContents);

      if (presentationTitle !== savedTitle) {
        setFieldValue(`courseContents.${index}.isAutoNamed`, false);
      }

      toast?.success(t('toast.success'), t('content.slides.save.success'));

      setTempSlidesByLanguage(slidesByLanguage);
      setSavedTitle(presentationTitle);
      onHide();
    } catch (e) {
      handleAxiosError(e as AxiosError, toast);
    }
  };

  const handleCopyToLanguage = (targetLanguages: LanguagesEnum[]) => {
    targetLanguages.forEach((targetLanguage) => {
      const currentSlides = slidesByLanguage[selectedLanguage] || [];

      const popupIdMapping: Record<string, string> = {};

      const copiedSlides = currentSlides.map((slide) => {
        const newId = `temp-${v4()}`;
        const newTempId = v4();

        if (slide.type === SlideTypeEnum.POPUP) {
          const oldId = slide.tempId || slide.id;

          if (oldId) {
            popupIdMapping[oldId] = newTempId;
          }
        }

        return {
          ...slide,
          id: newId,
          tempId: newTempId,
          position: slide.position || 0,
        };
      });

      // Update popup slide references using the mapping
      const updatedCopiedSlides = copiedSlides.map((slide) => {
        const updatedSlide = { ...slide };

        if (slide.popupSlide) {
          const oldPopupId =
            typeof slide.popupSlide === 'string'
              ? slide.popupSlide
              : slide.popupSlide.id;

          if (oldPopupId) {
            updatedSlide.tempPopupSlideId = popupIdMapping[oldPopupId];
            updatedSlide.popupSlide = undefined;
          }
        } else if (slide.tempPopupSlideId) {
          updatedSlide.tempPopupSlideId =
            popupIdMapping[slide.tempPopupSlideId];
        }

        return updatedSlide;
      });

      setSlidesByLanguage((prev) => ({
        ...prev,
        [targetLanguage]: updatedCopiedSlides,
      }));
    });

    toast?.success(t('toast.success'), t('content.all.slides.copied.success'));
  };
  const hasUnsavedChanges =
    JSON.stringify(slidesByLanguage) !== JSON.stringify(tempSlidesByLanguage) ||
    presentationTitle !== savedTitle;
  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      onHide();
    }
  };

  const handleDiscard = () => {
    setSlidesByLanguage(tempSlidesByLanguage);
    setPresentationTitle(savedTitle);
    onHide();
  };

  return {
    sensors,
    slides,
    mainSlides,
    popUpSlides,
    currentSlideData,
    currentEditingSlide,
    selectedLanguage,
    currentSlideIdx,
    setCurrentSlideIdx,
    selectedSlideId,
    setSelectedSlideId,
    isEditingPopUpSlide,
    setIsEditingPopUpSlide,
    isAnyDragging,
    isPurpleBackground,
    isSaveDisabled,
    loadFailed,
    retryLoad,
    showUnsavedChangesModal,
    setShowUnsavedChangesModal,
    presentationTitle,
    setPresentationTitle,
    handleClose,
    handleDiscard,
    handleSave,
    handleLanguageChange,
    handlePrevSlide,
    handleNextSlide,
    handleCopyToLanguage,
    handleSlidesDragStart,
    handleSlidesDragEnd,
    handleSlidesDragCancel,
    addSlide,
    deleteSlide,
    updateSlide,
    isLoading: loadingSlides,
    isDirty: hasUnsavedChanges,
  };
};
